import React from 'react';
import { ArrowDownIcon, ArrowUpIcon, FolderIcon, StarIcon } from 'lucide-react';
import { ItemMenu, type ItemActions } from './ItemMenu';
import { FILE_TYPE_META } from '../../utils/fileTypes';
import { folderStats, isFile, pathTo } from '../../utils/fileTree';
import { formatDateTime, formatSize } from '../../utils/format';
import type { FsNode, SortKey, SortState } from '../../types/files';
interface FileListProps {
  nodes: FsNode[];
  rows: FsNode[];
  selectedIds: string[];
  activeId: string | null;
  allRowsSelected: boolean;
  someRowsSelected: boolean;
  sort: SortState;
  compact: boolean;
  isTrash: boolean;
  showLocation: boolean;
  actions: ItemActions;
  onToggleRow: (id: string) => void;
  onToggleAll: () => void;
  onSort: (key: SortKey) => void;
  onDragStart: (node: FsNode) => void;
  onDragEnd: () => void;
  onDropOnFolder: (targetId: string) => void;
  draggingIds: string[];
}
const COLUMNS: {
  key: SortKey;
  label: string;
  className: string;
}[] = [{
  key: 'name',
  label: 'Name',
  className: ''
}, {
  key: 'owner',
  label: 'Owner',
  className: 'hidden lg:table-cell w-40'
}, {
  key: 'modifiedAt',
  label: 'Last Modified',
  className: 'hidden sm:table-cell w-48'
}, {
  key: 'sizeKb',
  label: 'Size',
  className: 'w-24 text-right'
}];
export function FileList({
  nodes,
  rows,
  selectedIds,
  activeId,
  allRowsSelected,
  someRowsSelected,
  sort,
  compact,
  isTrash,
  showLocation,
  actions,
  onToggleRow,
  onToggleAll,
  onSort,
  onDragStart,
  onDragEnd,
  onDropOnFolder,
  draggingIds
}: FileListProps) {
  const rowPadding = compact ? 'py-1.5' : 'py-2.5';
  return <table className="w-full border-collapse text-sm" data-id="element-74" data-mp="11gpk0l12zqlh">
      <caption className="sr-only" data-id="element-75" data-mp="16fak8kgc2a9x">
        Files and folders. Select rows to download as a zip, move, or delete.
      </caption>
      <thead data-id="element-76" data-mp="1u43kgt3zn4ac">
        <tr className="border-b border-slate-200 bg-white text-left" data-id="element-77" data-mp="1u3jzt4mye1sm">
          <th scope="col" className="w-10 px-4 py-2" data-id="element-78" data-mp="1yx5238vkxhxm">
            <input type="checkbox" checked={allRowsSelected} ref={node => {
            if (node) node.indeterminate = someRowsSelected;
          }} onChange={onToggleAll} aria-label="Select all items" className="h-4 w-4 rounded border-slate-400 text-[#0176d3] focus:ring-[#0176d3]" data-id="element-79" data-mp="28sdeikkk10el" />
          </th>
          {COLUMNS.map(column => {
          const isSorted = sort.key === column.key;
          return <th key={column.key} scope="col" aria-sort={isSorted ? sort.direction === 'asc' ? 'ascending' : 'descending' : 'none'} className={`px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500 ${column.className}`} data-id="element-80" data-mp="1l2izbwcrizr6">
                <button type="button" onClick={() => onSort(column.key)} className={['inline-flex items-center gap-1 rounded hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3]', column.key === 'sizeKb' ? 'ml-auto' : ''].join(' ')} data-id="element-81" data-mp="124ou7ter98e2">
                  {column.label}
                  {isSorted && (sort.direction === 'asc' ? <ArrowUpIcon className="h-3 w-3" aria-hidden="true" data-id="element-82" data-mp="2uyxx491rs70l" /> : <ArrowDownIcon className="h-3 w-3" aria-hidden="true" data-id="element-83" data-mp="2zxhh43wsz8qb" />)}
                </button>
              </th>;
        })}
          <th scope="col" className="w-12 px-3 py-2" data-id="element-84" data-mp="38tqivp896jfm">
            <span className="sr-only" data-id="element-85" data-mp="2caj8fsexd52y">Row actions</span>
          </th>
        </tr>
      </thead>
      <tbody data-id="element-86" data-mp="2gbmn4bl8kv2l">
        {rows.map(node => {
        const isSelected = selectedIds.includes(node.id);
        const isActive = activeId === node.id;
        const folder = node.kind === 'folder';
        const stats = folder ? folderStats(nodes, node.id) : null;
        const meta = isFile(node) ? FILE_TYPE_META[node.type] : null;
        const Icon = meta?.icon ?? FolderIcon;
        const location = pathTo(nodes, node.id);
        const canDrop = folder && draggingIds.length > 0 && !draggingIds.includes(node.id);
        return <tr key={node.id} draggable={!isTrash} onDragStart={() => onDragStart(node)} onDragEnd={onDragEnd} onDragOver={event => {
          if (canDrop) event.preventDefault();
        }} onDrop={event => {
          if (!canDrop) return;
          event.preventDefault();
          onDropOnFolder(node.id);
        }} onClick={() => actions.onOpen(node)} className={['group cursor-pointer border-b border-slate-100 transition', isSelected ? 'bg-[#eef4ff]' : isActive ? 'bg-slate-50' : 'bg-white hover:bg-slate-50'].join(' ')} data-id="element-87" data-mp="2fzkakfehgsn2">
              <td className={`px-4 ${rowPadding} align-middle`} data-id="element-88" data-mp="2xf6e17mg577e">
                <input type="checkbox" checked={isSelected} onClick={event => event.stopPropagation()} onChange={() => onToggleRow(node.id)} aria-label={`Select ${node.name}`} className="h-4 w-4 rounded border-slate-400 text-[#0176d3] focus:ring-[#0176d3]" data-id="element-89" data-mp="2xdd3hh8pq2u4" />
              </td>
              <td className={`px-3 ${rowPadding} align-middle`} data-id="element-90" data-mp="2eiodc0pmdwz0">
                <div className="flex items-center gap-2.5" data-id="element-91" data-mp="7rfhad01zs4t">
                  {isFile(node) && node.thumbnailUrl ? <img src={node.thumbnailUrl} alt="" className="h-8 w-10 shrink-0 rounded object-cover ring-1 ring-slate-200" data-id="element-92" data-mp="cofmklp2wquv" /> : <Icon className={['h-5 w-5 shrink-0', folder ? 'text-[#0176d3]' : meta?.iconClass ?? ''].join(' ')} aria-hidden="true" data-id="element-93" data-mp="19m0bhaj7ud5" />}
                  <div className="min-w-0" data-id="element-94" data-mp="ozq5vu7a646s">
                    <div className="flex items-center gap-1.5" data-id="element-95" data-mp="ozyirm3el9hz">
                      <button type="button" onClick={event => {
                    event.stopPropagation();
                    actions.onOpen(node);
                  }} className="max-w-[22rem] truncate text-left font-medium text-[#0176d3] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3]" data-id="element-96" data-mp="k880mty85hg0">
                        {node.name}
                      </button>
                      {node.starred && <StarIcon className="h-3.5 w-3.5 shrink-0 fill-amber-400 text-amber-500" aria-label="Starred" data-id="element-97" data-mp="1kammolxg2l7q" />}
                      {isFile(node) && <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-slate-600" data-id="element-98" data-mp="1z4rjzb2zy9mi">
                          {node.extension}
                        </span>}
                    </div>
                    <p className="truncate text-xs text-slate-500" data-id="element-99" data-mp="2cx7mmarzk5mw">
                      {folder ? `${stats?.fileCount ?? 0} files · ${formatSize(stats?.sizeKb ?? 0)}` : showLocation && location.length > 0 ? location.map(item => item.name).join(' / ') : showLocation ? 'Loan Documents' : `${node.owner} · v${node.version}`}
                    </p>
                  </div>
                </div>
              </td>
              <td className={`hidden px-3 ${rowPadding} align-middle text-slate-700 lg:table-cell`} data-id="element-100" data-mp="3h68ps8g624u3">
                {node.owner}
              </td>
              <td className={`hidden px-3 ${rowPadding} align-middle text-slate-600 sm:table-cell`} data-id="element-101" data-mp="2oj5stkr5225y">
                <span className="block" data-id="element-102" data-mp="2key3pmgrqk77">{formatDateTime(node.modifiedAt)}</span>
                <span className="block text-xs text-slate-500" data-id="element-103" data-mp="2y9gzhf27ipgj">
                  by {node.modifiedBy}
                </span>
              </td>
              <td className={`px-3 ${rowPadding} align-middle text-right tabular-nums text-slate-700`} data-id="element-104" data-mp="25rld3jc3to9y">
                {folder ? '—' : formatSize((node as {
              sizeKb: number;
            }).sizeKb)}
              </td>
              <td className={`px-3 ${rowPadding} align-middle`} onClick={event => event.stopPropagation()} data-id="element-105" data-mp="g9y18pd23wxr">
                <div className="flex justify-end" data-id="element-106" data-mp="7ab80quhdxfs">
                  <ItemMenu node={node} isTrash={isTrash} actions={actions} data-id="element-107" data-mp="7bmdcv3krd90" />
                </div>
              </td>
            </tr>;
      })}
      </tbody>
    </table>;
}